import { useState, type ChangeEvent, type ComponentType, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Input, Modal } from "antd";
import { journalsQueries } from "@/entities/journal";

interface RejectModalProps {
  open: boolean;
  rejectComment: string;
  rejectCommentError: string;
  sectionThreeListId: string;
  debouncedSearch: string;
  page: number;
  limit: number;
  selectedStatuses: string[];
  dateStart: string;
  dateEnd: string;
  workTypeFilterId: string;
  idFilterId: string;
  signerFilterId: string;
  createDate: string;
  updateDate: string;
  onCancel: () => void;
  onCommentChange: (comment: string) => void;
  onReject: (setError: (error: string) => void) => Promise<void>;
}

const getLeftFooter = (
  _: unknown,
  { OkBtn, CancelBtn }: { OkBtn: ComponentType; CancelBtn: ComponentType },
): ReactNode => (
  <div className="journal-modal-footer-left">
    <OkBtn />
    <CancelBtn />
  </div>
);

const getRefreshQueryKey = ({
  sectionThreeListId,
  debouncedSearch,
  page,
  limit,
  selectedStatuses,
  dateStart,
  dateEnd,
  workTypeFilterId,
  idFilterId,
  signerFilterId,
  createDate,
  updateDate,
}: {
  sectionThreeListId: string;
  debouncedSearch: string;
  page: number;
  limit: number;
  selectedStatuses: string[];
  dateStart: string;
  dateEnd: string;
  workTypeFilterId: string;
  idFilterId: string;
  signerFilterId: string;
  createDate: string;
  updateDate: string;
}) => {
  return journalsQueries.getSection3({
    sectionThreeListId,
    search: debouncedSearch,
    page,
    limit,
    status: selectedStatuses.join(","),
    dateFrom: dateStart,
    dateTo: dateEnd,
    workType: workTypeFilterId,
    actConnect: idFilterId,
    signerId: signerFilterId,
    createDate,
    updateDate,
  }).queryKey;
};

const validateComment = (comment: string): boolean => {
  return comment.trim().length > 0;
};

const handleTextAreaChange = (
  e: ChangeEvent<HTMLTextAreaElement>,
  onCommentChange: (comment: string) => void,
  onSetError: (error: string) => void,
) => {
  const newComment = e.target.value;
  onCommentChange(newComment);
  // Clear error when user starts typing
  if (onSetError) onSetError("");
};

export const RejectModal = ({
  open,
  rejectComment,
  rejectCommentError,
  sectionThreeListId,
  debouncedSearch,
  page,
  limit,
  selectedStatuses,
  dateStart,
  dateEnd,
  workTypeFilterId,
  idFilterId,
  signerFilterId,
  createDate,
  updateDate,
  onCancel,
  onCommentChange,
  onReject,
}: RejectModalProps) => {
  const queryClient = useQueryClient();
  const [localError, setLocalError] = useState("");

  const handleOk = async () => {
    if (!validateComment(rejectComment)) {
      setLocalError("Укажите причину отклонения");
      return;
    }

    await onReject(setLocalError);

    // Refresh the data
    queryClient.invalidateQueries({
      queryKey: getRefreshQueryKey({
        sectionThreeListId,
        debouncedSearch,
        page,
        limit,
        selectedStatuses,
        dateStart,
        dateEnd,
        workTypeFilterId,
        idFilterId,
        signerFilterId,
        createDate,
        updateDate,
      }),
    });
  };

  const displayError = localError || rejectCommentError;

  return (
    <Modal
      centered
      title="Отклонить подписание"
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Отклонить"
      cancelText="Отмена"
      destroyOnClose
      width={480}
      footer={getLeftFooter}
    >
      <div className="journal-reject-modal-intro">
        Вы действительно хотите отклонить подписание?
      </div>
      <Form layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item
          label="Комментарий"
          validateStatus={displayError ? "error" : ""}
          help={displayError || undefined}
          style={{ marginBottom: 16 }}
        >
          <Input.TextArea
            rows={3}
            value={rejectComment}
            onChange={(e) => handleTextAreaChange(e, onCommentChange, setLocalError)}
            maxLength={255}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
